import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ModpackNote } from '../types/modpackNote';
import { ServerConfig } from '../types/player';
import { modpackNotesApiService } from '../services/modpackNotesApi';
import { configService } from '../services/usersApi';
import { clusterManagementService } from '../services/clusterManagementService';
import { usernameCacheService } from '../services/usernameCache';
import { ModpackNoteCard } from '../components/ModpackNoteCard';
import { CreateModpackNoteModal } from '../components/CreateModpackNoteModal';
import { ModpackNoteDetailModal } from '../components/ModpackNoteDetailModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { MarkdownRenderer } from '../components/MarkdownRenderer';
import { CreatorProfile } from '../components/CreatorProfile';
import {
  StickyNote,
  Plus,
  Search,
  Loader,
  AlertCircle,
  Server,
  User,
  Globe,
  Pencil,
  Trash2,
  Info,
  List,
  LayoutGrid,
  Clock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDebounce } from '../hooks/useDebounce';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';
import { AnimatePresence } from 'framer-motion';
import { PageHeader } from '../components/ui/PageHeader';
import { format, formatDistanceToNow } from 'date-fns';

type ViewMode = 'all' | 'cluster' | 'fqdn' | 'mine';
type LayoutMode = 'timeline' | 'grid';

export const ModpackNotesView = () => {
  const { identity } = useAuth();
  const isMobile = useIsMobile();
  useDocumentTitle('Modpack Notes');

  const isDev = import.meta.env.DEV;

  const canWrite = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_modpack_write') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canEdit = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_modpack_edit') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  const canDelete = useMemo(() => {
    if (isDev) return true;
    if (!identity) return false;
    const groups = identity.raw.custom.groups;
    return groups.includes('argus_modpack_delete') || groups.includes('SPY_Super_admin');
  }, [identity, isDev]);

  // State
  const [notes, setNotes] = useState<ModpackNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => {
    return (localStorage.getItem('modpackNotes-layout') as LayoutMode) || 'timeline';
  });
  const [selectedCluster, setSelectedCluster] = useState('');
  const [selectedFqdn, setSelectedFqdn] = useState('');
  const [fqdnFilterText, setFqdnFilterText] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ModpackNote | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ModpackNote | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Cluster data
  const [configs, setConfigs] = useState<ServerConfig[]>([]);
  const [clusterFqdnMap, setClusterFqdnMap] = useState<Record<string, string[]>>({});

  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const PAGE_SIZE = 50;

  const debouncedSearch = useDebounce(searchQuery, 400);

  // Persist layout preference
  useEffect(() => {
    localStorage.setItem('modpackNotes-layout', layoutMode);
  }, [layoutMode]);

  const clusterThumbnails = useMemo(() => {
    const map: Record<string, string> = {};
    configs.forEach((c) => {
      if (c.cluster_name && c.thumbnail_url && !map[c.cluster_name]) {
        map[c.cluster_name] = c.thumbnail_url;
      }
    });
    return map;
  }, [configs]);

  const uniqueClusters = useMemo(
    () =>
      configs
        .reduce((acc, current) => {
          if (current.cluster_name && !acc.find((item) => item.cluster_name === current.cluster_name)) {
            acc.push(current);
          }
          return acc;
        }, [] as ServerConfig[])
        .sort((a, b) => a.cluster_name.localeCompare(b.cluster_name)),
    [configs]
  );

  const fqdnClusterMap = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(clusterFqdnMap).forEach(([cluster, fqdns]) => {
      fqdns.forEach(f => { map[f] = cluster; });
    });
    return map;
  }, [clusterFqdnMap]);

  // Date-grouped notes for timeline view
  const groupedNotes = useMemo(() => {
    if (notes.length === 0) return [];

    const today = format(new Date(), 'yyyy-MM-dd');
    const yesterday = format(new Date(Date.now() - 86400000), 'yyyy-MM-dd');

    const dateMap = new Map<string, ModpackNote[]>();
    const dateOrder: string[] = [];

    notes.forEach(note => {
      const dateKey = format(new Date(note.created_at), 'yyyy-MM-dd');
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, []);
        dateOrder.push(dateKey);
      }
      dateMap.get(dateKey)!.push(note);
    });

    return dateOrder.map(dateKey => ({
      dateKey,
      label: dateKey === today
        ? 'Today'
        : dateKey === yesterday
          ? 'Yesterday'
          : format(new Date(dateKey + 'T12:00:00'), 'EEEE, MMMM d, yyyy'),
      notes: dateMap.get(dateKey)!,
    }));
  }, [notes]);

  const lastNoteId = notes.length > 0 ? notes[notes.length - 1].id : null;

  // Load cluster data
  useEffect(() => {
    const loadClusterData = async () => {
      try {
        const [serverConfigs, fqdnMapping] = await Promise.all([
          configService.getConfigs(),
          clusterManagementService.getClusterFqdnMapping(),
        ]);
        setConfigs(serverConfigs);
        setClusterFqdnMap(fqdnMapping);
      } catch (err) {
        console.error('Failed to load cluster data:', err);
      }
    };
    loadClusterData();
  }, []);

  // Prefetch usernames
  useEffect(() => {
    if (notes.length === 0) return;
    const uuids = new Set<string>();
    notes.forEach((n) => {
      if (n.creator_id) uuids.add(n.creator_id);
      if (n.updated_by) uuids.add(n.updated_by);
    });
    usernameCacheService.prefetchUsernames(Array.from(uuids));
  }, [notes]);

  const fetchNotes = useCallback(
    async (pageNum: number = 1, append: boolean = false) => {
      if (!append) setLoading(true);
      setError(null);

      try {
        let fetchedNotes: ModpackNote[] = [];

        if (debouncedSearch.trim()) {
          const response = await modpackNotesApiService.searchNotes(
            debouncedSearch,
            pageNum,
            PAGE_SIZE
          );
          fetchedNotes = response.results || [];
        } else if (viewMode === 'cluster' && selectedCluster) {
          const fqdns = clusterFqdnMap[selectedCluster] || [];
          const response = await modpackNotesApiService.getAllClusterNotes(
            selectedCluster,
            fqdns,
            pageNum,
            PAGE_SIZE
          );
          fetchedNotes = response.results || [];
        } else if (viewMode === 'fqdn' && selectedFqdn) {
          const response = await modpackNotesApiService.getNotesByFqdn(
            selectedFqdn,
            pageNum,
            PAGE_SIZE
          );
          fetchedNotes = response.results || [];
        } else if (viewMode === 'mine' && identity) {
          const creatorId = identity.raw.custom.minecraft_uuid;
          const response = await modpackNotesApiService.getNotesByCreator(
            creatorId,
            pageNum,
            PAGE_SIZE
          );
          fetchedNotes = response.results || [];
        } else if (viewMode === 'all') {
          const response = await modpackNotesApiService.getAllNotes(pageNum, PAGE_SIZE);
          fetchedNotes = response.results || [];
        }

        if (append) {
          setNotes((prev) => [...prev, ...fetchedNotes]);
        } else {
          setNotes(fetchedNotes);
        }

        setHasMore(fetchedNotes.length >= PAGE_SIZE);
        setPage(pageNum);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load modpack notes');
        if (!append) setNotes([]);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch, viewMode, selectedCluster, selectedFqdn, identity, clusterFqdnMap]
  );

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchNotes(1, false);
  }, [fetchNotes]);

  // Infinite scroll
  const lastNoteRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchNotes(page + 1, true);
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore, page, fetchNotes]
  );

  // Handlers
  const handleNoteCreated = () => {
    setIsCreateModalOpen(false);
    setPage(1);
    fetchNotes(1, false);
  };

  const handleNoteUpdated = (updatedNote: ModpackNote) => {
    setNotes((prev) =>
      prev.map((n) => (n.id === updatedNote.id ? updatedNote : n))
    );
    setSelectedNote(updatedNote);
  };

  const handleNoteDeleted = (noteId: string) => {
    setNotes((prev) => prev.filter((n) => n.id !== noteId));
    setSelectedNote(null);
  };

  const handleDeleteFromCard = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await modpackNotesApiService.deleteNote(deleteTarget.id);
      handleNoteDeleted(deleteTarget.id);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete note');
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode !== 'cluster') setSelectedCluster('');
    if (mode !== 'fqdn') {
      setSelectedFqdn('');
      setFqdnFilterText('');
    }
    setSearchQuery('');
  };

  const emptyMessage = searchQuery
    ? 'No notes match your search'
    : viewMode === 'cluster' && !selectedCluster
      ? 'Select a cluster above to view its notes'
      : viewMode === 'fqdn' && !selectedFqdn
        ? 'Select a server above to view its notes'
        : viewMode === 'mine'
          ? "You haven't created any modpack notes yet"
          : 'No modpack notes found';

  // Timeline note renderer
  const renderTimelineNote = (note: ModpackNote) => {
    const thumbnailUrl = note.cluster_id ? clusterThumbnails[note.cluster_id] : undefined;
    const previewContent = note.content.length > 400
      ? note.content.slice(0, 400) + '…'
      : note.content;
    const hasMetadata = note.metadata !== null && note.metadata !== '';

    return (
      <div
        key={note.id}
        ref={note.id === lastNoteId ? lastNoteRef : null}
        className="group relative cursor-pointer"
        onClick={() => setSelectedNote(note)}
      >
        {/* Timeline dot - desktop only */}
        {!isMobile && (
          <div className="absolute left-[-24px] top-[16px] w-[7px] h-[7px] rounded-full bg-glass-400/40 group-hover:bg-accent-400 transition-colors ring-2 ring-dark-900/80" />
        )}

        <div className={cn(
          'rounded-lg p-3 min-w-0 transition-colors',
          'bg-dark-900/40 border border-glass-200/8 hover:border-accent-500/25 hover:bg-glass-200/5',
          isMobile && 'border-l-2 border-l-accent-500/30'
        )}>
          {/* Top row: badges + time + actions */}
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div className="flex items-center flex-wrap gap-1.5 min-w-0">
              {note.cluster_id && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-500/15 text-purple-300 border border-purple-500/20">
                  {thumbnailUrl ? (
                    <img
                      src={thumbnailUrl}
                      alt=""
                      className="w-3.5 h-3.5 rounded-sm object-cover"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  ) : (
                    <Server className="w-3 h-3" />
                  )}
                  {note.cluster_id}
                </span>
              )}
              {note.fqdn && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-accent-500/15 text-accent-300 border border-accent-500/20">
                  <Globe className="w-3 h-3" />
                  <span className="truncate max-w-[200px]">{note.fqdn}</span>
                </span>
              )}
              {hasMetadata && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] bg-amber-500/10 text-amber-400 border border-amber-500/15"
                  title="Has metadata"
                >
                  <Info className="w-3 h-3" />
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-[11px] text-glass-400 whitespace-nowrap">
                {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
              </span>
              <div className={cn(
                'flex items-center gap-0.5',
                !isMobile && 'opacity-0 group-hover:opacity-100 transition-opacity'
              )}>
                {canEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedNote(note); }}
                    className="p-1 rounded-md hover:bg-glass-200/20 text-glass-400 hover:text-accent-300 transition-colors touch-manipulation"
                    title="Edit"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(note); }}
                    className="p-1 rounded-md hover:bg-red-500/15 text-glass-400 hover:text-red-400 transition-colors touch-manipulation"
                    title="Delete"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Content preview */}
          <div className="text-sm text-glass-200 line-clamp-3 mb-1.5">
            <MarkdownRenderer
              content={previewContent}
              className="[&_p]:my-0.5 [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-xs"
            />
          </div>

          {/* Footer */}
          <div className="flex items-center gap-2 text-[11px] text-glass-500">
            <span>by</span>
            <CreatorProfile creatorId={note.creator_id} inline />
            {note.updated_at && (
              <>
                <span className="text-glass-600">·</span>
                <Clock className="w-3 h-3 flex-shrink-0" />
                <span>edited {formatDistanceToNow(new Date(note.updated_at), { addSuffix: true })}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Infrastructure"
        title="Modpack Notes"
        description="Track modpack changes, configuration updates, and known issues across clusters."
        icon={StickyNote}
        actions={canWrite ? (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            Create Note
          </button>
        ) : undefined}
      />

      {/* Toolbar */}
      <div className="glass-card p-3 md:p-4">

        {/* View Mode Toggle + Layout Toggle */}
        <div className="flex items-center gap-2 mb-4">
          <div className="segmented">
          {([
            { mode: 'all' as const, label: 'All', icon: StickyNote },
            { mode: 'cluster' as const, label: 'Cluster', icon: Server },
            { mode: 'fqdn' as const, label: 'Server', icon: Globe },
            { mode: 'mine' as const, label: 'Mine', icon: User },
          ]).map(({ mode, label, icon: Icon }) => (
            <button
              key={mode}
              onClick={() => handleViewModeChange(mode)}
              className="segmented-item"
              data-active={viewMode === mode ? 'true' : 'false'}
            >
              <Icon className="w-4 h-4" />
              <span className={cn(isMobile && mode !== viewMode && 'hidden')}>{label}</span>
            </button>
          ))}
          </div>

          <div className="flex-1" />

          {/* Layout toggle */}
          <div className="segmented">
            <button
              onClick={() => setLayoutMode('timeline')}
              className="segmented-item segmented-item-sm !px-2"
              data-active={layoutMode === 'timeline' ? 'true' : 'false'}
              title="Timeline view"
              aria-label="Timeline view"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setLayoutMode('grid')}
              className="segmented-item segmented-item-sm !px-2"
              data-active={layoutMode === 'grid' ? 'true' : 'false'}
              title="Grid view"
              aria-label="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Cluster Selector (cluster mode) */}
        {viewMode === 'cluster' && (
          <div className="mb-4">
            {isMobile ? (
              <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide -mx-1 px-1">
                {uniqueClusters.map((config) => (
                  <button
                    key={config.cluster_name}
                    onClick={() => setSelectedCluster(config.cluster_name)}
                    className={cn(
                      'flex-shrink-0 p-2 rounded-lg transition-all duration-200 border-2 touch-manipulation',
                      selectedCluster === config.cluster_name
                        ? 'bg-accent-500/30 border-accent-400'
                        : 'bg-glass-100/20 border-transparent active:border-accent-500/50'
                    )}
                  >
                    <img
                      src={config.thumbnail_url}
                      alt={config.cluster_name}
                      className="w-10 h-10 object-cover rounded-md"
                    />
                    <p className="text-center text-[10px] mt-1 text-white truncate w-10">
                      {config.cluster_name}
                    </p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-3">
                {uniqueClusters.map((config) => (
                  <button
                    key={config.cluster_name}
                    onClick={() => setSelectedCluster(config.cluster_name)}
                    className={cn(
                      'p-2 rounded-lg transition-all duration-200 border-2',
                      selectedCluster === config.cluster_name
                        ? 'bg-accent-500/30 border-accent-400'
                        : 'bg-glass-100/20 border-transparent hover:border-accent-500/50'
                    )}
                  >
                    <img
                      src={config.thumbnail_url}
                      alt={config.cluster_name}
                      className="w-12 h-12 object-cover rounded-md mx-auto"
                    />
                    <p className="text-center text-xs mt-1 text-white truncate">
                      {config.cluster_name}
                    </p>
                  </button>
                ))}
              </div>
            )}
            {!selectedCluster && (
              <p className="text-sm text-glass-500 mt-2">Select a cluster to view its notes.</p>
            )}
          </div>
        )}

        {/* FQDN Selector (server mode) */}
        {viewMode === 'fqdn' && (
          <div className="mb-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
              <input
                type="text"
                value={fqdnFilterText}
                onChange={(e) => setFqdnFilterText(e.target.value)}
                placeholder="Search for a server..."
                className="glass-input w-full pl-10 text-sm"
              />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-3 pr-1">
              {Object.entries(clusterFqdnMap)
                .filter(([, fqdns]) =>
                  fqdns.some(f =>
                    !fqdnFilterText || f.toLowerCase().includes(fqdnFilterText.toLowerCase())
                  )
                )
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([cluster, fqdns]) => {
                  const filtered = fqdns.filter(f =>
                    !fqdnFilterText || f.toLowerCase().includes(fqdnFilterText.toLowerCase())
                  );
                  if (filtered.length === 0) return null;
                  return (
                    <div key={cluster}>
                      <div className="text-[10px] font-semibold text-glass-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                        {clusterThumbnails[cluster] && (
                          <img
                            src={clusterThumbnails[cluster]}
                            alt=""
                            className="w-3.5 h-3.5 rounded-sm object-cover"
                          />
                        )}
                        {cluster}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {filtered.sort().map(f => (
                          <button
                            key={f}
                            onClick={() => setSelectedFqdn(selectedFqdn === f ? '' : f)}
                            className={cn(
                              'px-2.5 py-1 rounded-md text-xs transition-all border',
                              selectedFqdn === f
                                ? 'bg-accent-500/15 text-accent-300 border-accent-500/25'
                                : 'bg-glass-100/10 text-glass-300 border-glass-200/10 hover:border-accent-500/20 hover:text-white'
                            )}
                          >
                            {f}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
            {!selectedFqdn && (
              <p className="text-xs text-glass-500">Select a server to view its notes.</p>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-glass-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="glass-input w-full pl-10"
            placeholder="Search modpack notes..."
          />
        </div>

        {/* Note count */}
        {notes.length > 0 && !loading && (
          <p className="mt-2 text-xs text-glass-500">
            {notes.length} note{notes.length !== 1 ? 's' : ''}{hasMore ? '+' : ''} loaded
          </p>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="glass-card p-4 border-l-4 border-red-400">
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300">{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && notes.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-accent-400" />
        </div>
      )}

      {/* Content */}
      {(!loading || notes.length > 0) ? (
        notes.length === 0 && !loading ? (
          /* Empty State */
          <div className="glass-card p-12 text-center">
            <StickyNote className="w-12 h-12 text-glass-400 mx-auto mb-4" />
            <p className="text-glass-400 text-lg mb-1">{emptyMessage}</p>
            <p className="text-glass-500 text-sm">
              {canWrite && !searchQuery && viewMode !== 'cluster' && viewMode !== 'fqdn'
                ? 'Click "Create Note" to add the first one.'
                : ''}
            </p>
          </div>
        ) : layoutMode === 'timeline' ? (
          /* Timeline View */
          <div>
            {/* Desktop timeline */}
            {!isMobile ? (
              <div className="relative pl-9">
                {/* Vertical line */}
                <div
                  className="absolute left-[15px] top-4 bottom-4 w-px"
                  style={{ background: 'linear-gradient(to bottom, rgba(145,94,255,0.3), rgba(125,83,222,0.12), transparent)' }}
                />

                {groupedNotes.map((group, gi) => (
                  <div key={group.dateKey} className={cn(gi > 0 && 'mt-5')}>
                    {/* Date header */}
                    <div className="relative flex items-center gap-3 mb-2.5">
                      <div className="absolute left-[-27px] top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-accent-500/30 border-2 border-accent-500/50" />
                      <span className="text-xs font-bold text-accent-300 uppercase tracking-wider">
                        {group.label}
                      </span>
                      <span className="text-[10px] text-glass-500">
                        {group.notes.length} note{group.notes.length !== 1 ? 's' : ''}
                      </span>
                      <div className="h-px flex-1 bg-glass-200/5" />
                    </div>

                    {/* Notes */}
                    <div className="space-y-1.5">
                      {group.notes.map(note => renderTimelineNote(note))}
                    </div>
                  </div>
                ))}

                {/* Loading more */}
                {loading && notes.length > 0 && (
                  <div className="flex items-center justify-center py-6">
                    <Loader className="w-6 h-6 animate-spin text-accent-400" />
                  </div>
                )}
              </div>
            ) : (
              /* Mobile timeline */
              <div className="space-y-1">
                {groupedNotes.map((group, gi) => (
                  <div key={group.dateKey} className={cn(gi > 0 && 'mt-3')}>
                    {/* Date header */}
                    <div className="flex items-center gap-2 py-2 sticky top-0 z-10 bg-dark-900/90 backdrop-blur-sm -mx-1 px-1">
                      <div className="h-px flex-1 bg-glass-200/10" />
                      <span className="text-[11px] font-bold text-accent-300 uppercase tracking-wider">
                        {group.label}
                      </span>
                      <span className="text-[10px] text-glass-500 bg-glass-200/10 px-1.5 py-0.5 rounded-full">
                        {group.notes.length}
                      </span>
                      <div className="h-px flex-1 bg-glass-200/10" />
                    </div>

                    {/* Notes */}
                    <div className="space-y-1.5">
                      {group.notes.map(note => renderTimelineNote(note))}
                    </div>
                  </div>
                ))}

                {/* Loading more */}
                {loading && notes.length > 0 && (
                  <div className="flex items-center justify-center py-6">
                    <Loader className="w-6 h-6 animate-spin text-accent-400" />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Grid View */
          <div className={cn(
            'grid gap-4',
            isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
          )}>
            <AnimatePresence>
              {notes.map((note, index) => (
                <div
                  key={note.id}
                  ref={note.id === lastNoteId ? lastNoteRef : null}
                >
                  <ModpackNoteCard
                    note={note}
                    index={index}
                    clusterThumbnails={clusterThumbnails}
                    canEdit={canEdit}
                    canDelete={canDelete}
                    onClick={() => setSelectedNote(note)}
                    onEdit={() => setSelectedNote(note)}
                    onDelete={() => setDeleteTarget(note)}
                  />
                </div>
              ))}
            </AnimatePresence>

            {loading && notes.length > 0 && (
              <div className="col-span-full flex items-center justify-center py-6">
                <Loader className="w-6 h-6 animate-spin text-accent-400" />
              </div>
            )}
          </div>
        )
      ) : null}

      {/* Create Modal */}
      <CreateModpackNoteModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleNoteCreated}
        preselectedCluster={
          viewMode === 'cluster'
            ? selectedCluster
            : viewMode === 'fqdn' && selectedFqdn
              ? fqdnClusterMap[selectedFqdn]
              : undefined
        }
        preselectedFqdn={viewMode === 'fqdn' ? selectedFqdn : undefined}
      />

      {/* Detail Modal */}
      {selectedNote && (
        <ModpackNoteDetailModal
          note={selectedNote}
          isOpen={!!selectedNote}
          clusterThumbnails={clusterThumbnails}
          onClose={() => setSelectedNote(null)}
          onUpdate={handleNoteUpdated}
          onDelete={handleNoteDeleted}
        />
      )}

      {/* Delete Confirmation from card */}
      {deleteTarget && (
        <ConfirmationModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteFromCard}
          title="Delete Modpack Note"
          message="Are you sure you want to permanently delete this modpack note? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};
